import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { getUsers, deleteUser, restoreUser } from "../api/userApi";
import { confirmDialog, showError } from "../utils/alert";
import Pagination, { DEFAULT_PER_PAGE } from "../components/Pagination";

export default function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage] = useState(DEFAULT_PER_PAGE);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await getUsers({ page, per_page: perPage, deleted: showDeleted ? 1 : 0 });
      setUsers(res.data.items);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, perPage, showDeleted]);

  const handleDelete = async (id) => {
    const confirmed = await confirmDialog({ text: "Delete this user?" });
    if (!confirmed) return;
    await deleteUser(id);
    loadUsers();
  };

  const handleRestore = async (id) => {
    const confirmed = await confirmDialog({ text: "Restore this deleted user?" });
    if (!confirmed) return;
    try {
      await restoreUser(id);
      loadUsers();
    } catch (err) {
      showError("Failed to restore user");
    }
  };

  return (
    <AdminLayout title="Users">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">
              {showDeleted ? "Deleted User List" : "User List"}
            </h5>
            <div className="d-flex gap-2">
              <button
                type="button"
                className={`btn ${showDeleted ? "btn-outline-secondary" : "btn-outline-danger"}`}
                onClick={() => {
                  setPage(1);
                  setShowDeleted((prev) => !prev);
                }}
                title={showDeleted ? "Back to active users" : "Show deleted users"}
              >
                {showDeleted ? (
                  <>
                    <i className="bi bi-arrow-left me-1" />
                    Back
                  </>
                ) : (
                  <>
                    <i className="bi bi-trash me-1" />
                    Trash
                  </>
                )}
              </button>
              {!showDeleted && (
                <button
                  className="btn skedq-accent-btn"
                  onClick={() => navigate("/users/new")}
                >
                  Add New User
                </button>
              )}
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  {showDeleted ? <th>Deleted At</th> : <th>Status</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={showDeleted ? "6" : "6"} className="text-center">
                      Loading...
                    </td>
                  </tr>
                )}
                {!loading && users.length === 0 && (
                  <tr>
                    <td colSpan={showDeleted ? "6" : "6"} className="text-center text-muted">
                      {showDeleted ? "No deleted users found." : "No users found."}
                    </td>
                  </tr>
                )}
                {!loading &&
                  users.map((u, index) => (
                    <tr key={u.id}>
                      <td>{(page - 1) * perPage + index + 1}</td>
                      <td>
                        {u.first_name} {u.last_name}
                      </td>
                      <td>{u.email}</td>
                      <td>{u.mobile}</td>
                      {showDeleted ? (
                        <>
                          <td>{u.deleted_at ? new Date(u.deleted_at).toLocaleString() : "-"}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleRestore(u.id)}
                            >
                              Restore
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{u.status === "1" ? "Active" : "Inactive"}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(`/users/detail/${u.id}`)}
                              >
                                View
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => navigate(`/users/edit/${u.id}`)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() => handleDelete(u.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            total={total}
            perPage={perPage}
            onPageChange={setPage}
          />
        </div>
      </div>
    </AdminLayout>
  );
}
